import type { ReactNode } from "react";
import type { RecentCallListItem } from "./recent-call-list";

// ─── Transcript rendering ────────────────────────────────────────────────────

const KEYS_MARKER = /\[keys:([^\]]+)\]/g;

type TranscriptPart = { type: "text"; content: string } | { type: "keys"; digits: string };

function parseTranscript(text: string): TranscriptPart[] {
  const parts: TranscriptPart[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  KEYS_MARKER.lastIndex = 0;
  while ((match = KEYS_MARKER.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", content: text.slice(lastIndex, match.index) });
    }
    parts.push({ type: "keys", digits: match[1] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push({ type: "text", content: text.slice(lastIndex) });
  }
  return parts;
}

export function TranscriptText({ text }: { text: string | null | undefined }): ReactNode {
  if (!text) return null;
  const parts = parseTranscript(text);
  return (
    <>
      {parts.map((part, i) =>
        part.type === "keys" ? (
          <span
            key={i}
            title={`Keys sent: ${part.digits}`}
            style={{
              display: "inline-block",
              background: "var(--cf-info)",
              color: "var(--cf-on-info)",
              borderRadius: "4px",
              padding: "0 5px",
              fontFamily: "monospace",
              fontWeight: 600,
              fontSize: "0.85em",
              margin: "0 3px",
              verticalAlign: "middle",
            }}
          >
            {part.digits}
          </span>
        ) : (
          <span key={i}>{part.content}</span>
        )
      )}
    </>
  );
}

export function transcriptTextOnly(text: string | null | undefined): string {
  if (!text) return "";
  return text.replace(KEYS_MARKER, "").replace(/\s+/g, " ").trim();
}

// ─── Call record type ────────────────────────────────────────────────────────

export type CallRecord = {
  id: string;
  startedAt: string;
  status: string;
  resultLabel: string | null;
  resultDate: string | null;
  transcript: string | null;
  recordingAvailable: boolean;
  recordingExpired: boolean;
  recordingUrl: string | null;
};

// ─── Date formatting ─────────────────────────────────────────────────────────

function ordinalSuffix(day: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = day % 100;
  return s[(v - 20) % 10] ?? s[v] ?? s[0]!;
}

function parseCallDate(value: string): Date | null {
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed;
  // Handle "YYYY-MM-DD HH:MM UTC" format
  const match = value.match(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}) UTC$/);
  if (!match) return null;
  const fallback = new Date(`${match[1]}T${match[2]}:00.000Z`);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

export function formatCallDate(value: string): string {
  const date = parseCallDate(value) ?? new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const day = date.getDate();
  const month = new Intl.DateTimeFormat(undefined, { month: "short" }).format(date);
  const year = new Intl.DateTimeFormat(undefined, { year: "numeric" }).format(date);
  const time = new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
    .format(date)
    .toLowerCase()
    .replace(" ", "");

  return `${month} ${day}${ordinalSuffix(day)}, ${year} ${time}`;
}

export function formatResultDate(value: string): string {
  // Date-only strings (YYYY-MM-DD) are parsed as UTC by JS, which shifts
  // to the previous day in timezones behind UTC. Treat them as local dates.
  const isoDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const date = isoDateOnly ? new Date(value + "T00:00:00") : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ─── Internal rendering ──────────────────────────────────────────────────────

function resolveResultLabel(record: CallRecord): string {
  const status = String(record.status ?? "")
    .trim()
    .toLowerCase();
  const isTerminal = status === "completed" || status === "failed";
  if (!isTerminal) return "Pending";
  return record.resultLabel ?? "Pending";
}

function buildTranscriptPreview(text: string | null | undefined): string {
  if (!text) return "";
  const plain = text
    .replace(/\[keys:[^\]]+\]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  // Find a meaningful excerpt — skip the IVR preamble and grab from the result portion
  const resultMarkers = [
    "you are scheduled",
    "you are not scheduled",
    "your client id",
    "please bring",
    "urine screen",
    "drug screen",
    "bottle check",
  ];
  const lower = plain.toLowerCase();
  for (const marker of resultMarkers) {
    const idx = lower.indexOf(marker);
    if (idx >= 0) {
      const excerpt = plain.slice(idx, idx + 120);
      return excerpt.length < plain.slice(idx).length ? excerpt + "…" : excerpt;
    }
  }
  // Fallback: last 120 characters
  if (plain.length > 120) {
    return "…" + plain.slice(-120);
  }
  return plain;
}

function CallRecordAudio({ record }: { record: CallRecord }): ReactNode {
  if (!record.recordingAvailable) return <span className="cf-help">No recording available.</span>;
  if (record.recordingExpired) return <span className="cf-help">Recording expired.</span>;
  if (!record.recordingUrl) return <span className="cf-help">No recording available.</span>;
  return (
    <a
      className="cf-btn cf-btn-sm cf-outline-neutral"
      href={record.recordingUrl}
      download={`${record.id}.mp3`}
    >
      Download audio
    </a>
  );
}

function CallRecordDetails({
  record,
  onClose,
}: {
  record: CallRecord;
  onClose?: () => void;
}): ReactNode {
  const resultLabel = resolveResultLabel(record);
  const resultDate = record.resultDate ? formatResultDate(record.resultDate) : null;
  const hasAudio = record.recordingAvailable && !record.recordingExpired && record.recordingUrl;

  return (
    <div className="cf-recent-call-detail-panel">
      {/* Summary block */}
      <div className="cf-recent-call-detail-summary">
        <div className="cf-recent-call-detail-summary-item">
          <span className="cf-recent-call-detail-summary-label">Result</span>
          <span className="cf-recent-call-detail-summary-value">{resultLabel}</span>
        </div>
        {resultDate && (
          <div className="cf-recent-call-detail-summary-item">
            <span className="cf-recent-call-detail-summary-label">For date</span>
            <span className="cf-recent-call-detail-summary-value">{resultDate}</span>
          </div>
        )}
        <div className="cf-recent-call-detail-summary-item">
          <span className="cf-recent-call-detail-summary-label">Called at</span>
          <span className="cf-recent-call-detail-summary-value">
            {formatCallDate(record.startedAt)}
          </span>
        </div>
      </div>

      {/* Full transcript */}
      <div className="cf-recent-call-detail-transcript">
        <p className="cf-recent-call-detail-transcript-label">Full transcript</p>
        <div className="cf-recent-call-detail-transcript-body">
          <TranscriptText text={record.transcript ?? "No transcript available yet."} />
        </div>
      </div>

      {/* Actions row */}
      <div className="cf-recent-call-detail-actions">
        {hasAudio ? (
          <a
            className="cf-btn cf-btn-sm cf-outline-neutral"
            href={record.recordingUrl!}
            download={`${record.id}.mp3`}
          >
            Download audio
          </a>
        ) : (
          <span className="cf-help">
            {record.recordingExpired ? "Recording expired." : "No recording available."}
          </span>
        )}
        {onClose && (
          <button
            type="button"
            className="cf-btn cf-btn-sm cf-outline-neutral cf-recent-call-detail-close"
            onClick={onClose}
          >
            Close details
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Public API ──────────────────────────────────────────────────────────────

export function buildCallListItem(
  record: CallRecord,
  options: {
    isExpanded: boolean;
    onToggle: () => void;
    rowRef?: (el: HTMLDivElement | null) => void;
  }
): RecentCallListItem {
  return {
    id: record.id,
    rowRef: options.rowRef,
    dateLabel: formatCallDate(record.startedAt),
    statusLabel: record.status,
    resultLabel: resolveResultLabel(record),
    transcriptPreview: buildTranscriptPreview(record.transcript),
    transcript: <TranscriptText text={record.transcript ?? "No transcript available yet."} />,
    audio: <CallRecordAudio record={record} />,
    detailsOpen: options.isExpanded,
    onToggleDetails: options.onToggle,
    details: <CallRecordDetails record={record} onClose={options.onToggle} />,
  };
}
